import { Box } from "@mantine/core";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLateFeeConfig } from "../../hooks/use-late-fee-config";
import { Dialog } from "../../utils/confirm-modal";
import { Page, PageHeader } from "../../components/layout/Page";
import { AutoStatementConfig } from "./auto-statement-config";
import { LateFeeConfig } from "./late-fee-config";
import { ReminderConfig } from "./reminder-config";
import classes from "./settings-ui.module.css";
import { useAutoStatementConfigData, useReminderConfigData } from "./use-settings-config";
import { useUnsavedGuard } from "./use-unsaved-guard";

const TABS = ["late-fee", "reminder", "auto-statement"] as const;
type TabKey = (typeof TABS)[number];

export function Settings() {
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState<TabKey>("late-fee");
  const [dirtyCount, setDirtyCount] = useState(0);
  const [asideTarget, setAsideTarget] = useState<HTMLElement | null>(null);

  // Цэсний pill нь master toggle-ийн ХАДГАЛСАН утга; табуудтай ижил SWR key.
  const lateFee = useLateFeeConfig();
  const reminder = useReminderConfigData();
  const autoStatement = useAutoStatementConfigData();

  const savedEnabled: Record<TabKey, boolean | undefined> = {
    "late-fee": (lateFee.data as any)?.config ? !!(lateFee.data as any).config.lateFeeEnabled : undefined,
    reminder: reminder.data?.config ? !!reminder.data.config.reminderEnabled : undefined,
    "auto-statement": autoStatement.data ? !!autoStatement.data.autoStatementEnabled : undefined,
  };

  const labels: Record<TabKey, string> = {
    "late-fee": t("tab.lateFee"),
    reminder: t("tab.reminder"),
    "auto-statement": t("tab.autoStatement"),
  };

  const leaveMessage = t("unsaved.leaveConfirm");
  useUnsavedGuard(dirtyCount > 0, leaveMessage);

  const handleTabChange = (key: TabKey) => {
    if (key === activeTab) return;
    if (dirtyCount === 0) {
      setActiveTab(key);
      return;
    }
    Dialog.confirm(leaveMessage, (answer) => {
      if (answer === "confirm") setActiveTab(key);
    });
  };

  const handleDirtyChange = useCallback((count: number) => setDirtyCount(count), []);
  const tabProps = { asideTarget, onDirtyChange: handleDirtyChange };

  return (
    <Page>
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle")}
      />
      <Box className={classes.layout}>
        <Box className={classes.sideNav} role="tablist" aria-orientation="vertical">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              data-active={activeTab === key || undefined}
              className={classes.sideTab}
              onClick={() => handleTabChange(key)}>
              {labels[key]}
              {savedEnabled[key] !== undefined && (
                <Box component="span" className={classes.pill} data-on={savedEnabled[key] || undefined}>
                  {savedEnabled[key] ? t("status.on") : t("status.off")}
                </Box>
              )}
            </button>
          ))}
        </Box>
        <Box role="tabpanel" miw={0}>
          {activeTab === "late-fee" && <LateFeeConfig {...tabProps} />}
          {activeTab === "reminder" && <ReminderConfig {...tabProps} />}
          {activeTab === "auto-statement" && <AutoStatementConfig {...tabProps} />}
        </Box>
        <Box component="aside" className={classes.aside} ref={setAsideTarget} />
      </Box>
    </Page>
  );
}
